// Progresso real das obras, derivado das tarefas do Gestor.
//
// Antes o card de /obras mostrava 50% para toda obra ativa e 100% para
// concluída — um número fixo com aparência de dado, que induzia decisão errada
// em quem olha o painel. Agora o percentual vem das tarefas vinculadas à obra
// (work_tags[0]); quando não há tarefas, devolvemos `null` em vez de inventar
// um valor, e a UI diz que não há como medir.
import { supabase } from "@/integrations/supabase/client";

export interface ProgressoObra {
  /** 0–100, ou null quando não há tarefas para medir. */
  percentual: number | null;
  concluidas: number;
  total: number;
}

const VAZIO: ProgressoObra = { percentual: null, concluidas: 0, total: 0 };

let _porObra = new Map<string, ProgressoObra>();

/**
 * Lê as tarefas uma vez e agrega por obra. Tarefas canceladas não entram na
 * conta: não foram feitas nem estão pendentes, então inflariam ou deflacionariam
 * o percentual sem significar avanço.
 */
export async function hydrateObraProgress(): Promise<void> {
  const { data, error } = await supabase
    .from("tasks")
    .select("status, work_tags");
  if (error) {
    console.error("hydrateObraProgress:", error.message);
    return;
  }

  const acc = new Map<string, { concluidas: number; total: number }>();
  for (const t of data ?? []) {
    const obraId = (t.work_tags ?? [])[0];
    if (!obraId) continue;
    if (t.status === "cancelled") continue;
    const atual = acc.get(obraId) ?? { concluidas: 0, total: 0 };
    atual.total += 1;
    if (t.status === "completed") atual.concluidas += 1;
    acc.set(obraId, atual);
  }

  _porObra = new Map(
    [...acc.entries()].map(([id, { concluidas, total }]) => [
      id,
      {
        concluidas,
        total,
        percentual: total > 0 ? Math.round((concluidas / total) * 100) : null,
      },
    ]),
  );
}

/** Progresso de uma obra. Obra sem tarefas devolve percentual null. */
export function getProgressoObra(obraId: string): ProgressoObra {
  return _porObra.get(obraId) ?? VAZIO;
}
